// One booking with all its services (one line per service, each with its own stylist, slot and price), shown
// in an appointment's details. From here the desk adds a service in the salon, stretches a line by one slot
// ("+1 slot", staff only), cancels one service or the whole booking, or moves the whole booking to other times.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api, { addDays, clockLabel, formatDateTime, formatTime, isoDate } from '../services/api';
import AddServiceModal from './AddServiceModal';
import { Confirm, Modal, Spinner } from './ui';

const BOOKING_STATUS = {
  booked: { label: 'Booked', tone: 'info' },
  in_salon: { label: 'In the salon', tone: 'warn' },
  completed: { label: 'Completed', tone: 'ok' },
  partly_completed: { label: 'Partly completed', tone: 'ok' },
  no_show: { label: 'No-show', tone: 'muted' },
  cancelled: { label: 'Cancelled', tone: 'muted' },
  visited_no_service: { label: 'Visited, no service', tone: 'muted' },
};
const LINE_STATUS = { booked: 'Booked', service_taken: 'Service taken', no_show: 'No-show', cancelled: 'Cancelled', not_taken: 'Visited, not taken' };

function MoveBookingModal({ booking, onClose, onMoved }) {
  const booked = booking.lines.filter((l) => l.status === 'booked');
  const [day, setDay] = useState(String(booked[0]?.starts_at || isoDate()).slice(0, 10));
  const [options, setOptions] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setOptions(null);
    setChosen(null);
    api
      .bookingOptions({ day, count: booked.length, franchise_id: booking.franchise_id })
      .then((d) => setOptions(d.options || []))
      .catch((e) => toast.error(e.message));
  }, [day]);
  const move = async () => {
    setBusy(true);
    try {
      await api.moveBooking(booking.id, chosen.lines.map((l) => l.slot_id), notify);
      toast.success(notify ? 'Booking moved; the customer gets one “time changed” message' : 'Booking moved');
      onMoved();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={`Move the booking (${booked.length} service${booked.length > 1 ? 's' : ''})`} onClose={onClose} size="wide">
      <div className="de-row" style={{ gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setDay(addDays(day, -1))} disabled={day <= isoDate()}>
          ←
        </button>
        <input type="date" className="de-input de-input--bordered" style={{ width: 170 }} value={day} min={isoDate()} onChange={(e) => setDay(e.target.value)} />
        <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setDay(addDays(day, 1))}>
          →
        </button>
      </div>
      {!options ? (
        <div className="de-loader">
          <Spinner /> Finding times…
        </div>
      ) : options.length ? (
        <div style={{ display: 'grid', gap: 6, maxHeight: 340, overflow: 'auto' }}>
          {options.map((o, i) => (
            <label key={i} className="de-card" style={{ padding: 10, cursor: 'pointer', outline: chosen === o ? '2px solid var(--de-gold)' : 'none' }}>
              <input type="radio" name="move-option" checked={chosen === o} onChange={() => setChosen(o)} style={{ marginRight: 8 }} />
              <b>{o.label}</b> – {clockLabel(o.ends)}
              <span className="de-small de-muted">
                {' · '}
                {o.lines.map((l, j) => `${booked[j]?.service_name || 'Service'} ${l.label} with ${l.with}`).join(', ')}
              </span>
            </label>
          ))}
        </div>
      ) : (
        <div className="de-callout">No free times that day for all the services. Try another day.</div>
      )}
      <label className="de-row de-mt-16" style={{ gap: 6, alignItems: 'center' }}>
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
        <span className="de-small">Tell the customer on WhatsApp</span>
      </label>
      <div className="de-form-actions" style={{ marginTop: 16 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={!chosen || busy} onClick={move}>
          {busy ? <Spinner light /> : null}
          Move
        </button>
      </div>
    </Modal>
  );
}

export default function BookingPanel({ bookingId, onChanged }) {
  const [booking, setBooking] = useState(null);
  const [busy, setBusy] = useState('');
  const [adding, setAdding] = useState(null); // { purpose, appointmentId, options }
  const [moving, setMoving] = useState(false);
  const [cancel, setCancel] = useState(null); // { line } or { all: true }
  const [reason, setReason] = useState('');

  const load = () =>
    api
      .getBooking(bookingId)
      .then(setBooking)
      .catch((e) => toast.error(e.message));

  useEffect(() => {
    load();
  }, [bookingId]);

  const changed = () => {
    load();
    onChanged && onChanged();
  };

  const extend = async (line) => {
    setBusy(`x${line.id}`);
    try {
      await api.extendAppointment(line.id);
      toast.success('One more slot added');
      changed();
    } catch (e) {
      if (e.status === 409 && e.data?.options) {
        setAdding({ purpose: 'extend', appointmentId: line.id, options: e.data.options });
      } else {
        toast.error(e.message);
      }
    } finally {
      setBusy('');
    }
  };

  const shorten = async (line) => {
    setBusy(`s${line.id}`);
    try {
      await api.shortenAppointment(line.id);
      toast.success('Extra slot given back');
      changed();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  const doCancel = async () => {
    setBusy('cancel');
    try {
      if (cancel.all) await api.cancelBooking(booking.id, reason);
      else await api.cancelBookingLine(cancel.line.id, reason);
      toast.success(cancel.all ? 'Booking cancelled' : `${cancel.line.service_name} cancelled`);
      setCancel(null);
      setReason('');
      changed();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  if (!booking) return null;
  const status = BOOKING_STATUS[booking.status] || { label: booking.status, tone: 'muted' };
  const active = booking.lines.filter((l) => l.status !== 'cancelled');
  const anyBooked = booking.lines.some((l) => l.status === 'booked');
  return (
    <div className="de-card de-mt-16" style={{ padding: 14 }}>
      <div className="de-row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <div className="de-td-strong">
          Booking #{booking.id} <span className={`de-pill de-pill--sm de-pill--${status.tone}`}>{status.label}</span>
          <span className="de-small de-muted" style={{ marginLeft: 8 }}>
            {active.length} service{active.length === 1 ? '' : 's'}
            {booking.total_price ? ` · ${booking.total_price}` : ''}
            {booking.starts_at ? ` · ${formatDateTime(booking.starts_at)}${booking.ends_at ? ` – ${formatTime(booking.ends_at)}` : ''}` : ''}
          </span>
        </div>
        {booking.status !== 'cancelled' ? (
          <div className="de-row" style={{ gap: 6 }}>
            <button type="button" className="de-btn de-btn--primary de-btn--sm" onClick={() => setAdding({ purpose: 'add_service' })}>
              + Add service
            </button>
            {anyBooked ? (
              <>
                <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setMoving(true)}>
                  Move booking
                </button>
                <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setCancel({ all: true })}>
                  Cancel booking
                </button>
              </>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className="de-table-wrap">
        <table className="de-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Service</th>
              <th>Stylist</th>
              <th>Time</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {booking.lines.map((l) => (
              <tr key={l.id} style={l.status === 'cancelled' ? { opacity: 0.55 } : undefined}>
                <td className="de-td-muted">{l.line_no}</td>
                <td>
                  <div className="de-td-strong">{l.service_name}</div>
                  <div className="de-small de-muted">
                    {l.service_price || ''}
                    {l.added_in_salon ? <span className="de-pill de-pill--sm de-pill--info" style={{ marginLeft: 6 }}>Added in salon</span> : null}
                    {l.pending_change ? <span className="de-pill de-pill--sm de-pill--warn" style={{ marginLeft: 6 }}>Asked to move: waiting for reply</span> : null}
                  </div>
                </td>
                <td>{l.staff_name || <span className="de-muted">Any stylist</span>}</td>
                <td className="de-small">
                  {formatTime(l.starts_at)}
                  {l.extra_slots ? <div className="de-muted">+{l.extra_slots} slot{l.extra_slots > 1 ? 's' : ''}</div> : null}
                </td>
                <td className="de-small">
                  {LINE_STATUS[l.status] || l.status}
                  {l.cancel_reason ? <div className="de-muted">{l.cancel_reason}</div> : null}
                </td>
                <td className="de-td-right" style={{ whiteSpace: 'nowrap' }}>
                  {l.status === 'booked' ? (
                    <div className="de-row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                      {l.staff_name ? (
                        <button type="button" className="de-btn de-btn--outline de-btn--xs" disabled={busy === `x${l.id}`} onClick={() => extend(l)} title="The service runs long: take the stylist's next slot">
                          +1 slot
                        </button>
                      ) : null}
                      {l.extra_slots ? (
                        <button type="button" className="de-btn de-btn--outline de-btn--xs" disabled={busy === `s${l.id}`} onClick={() => shorten(l)}>
                          −1 slot
                        </button>
                      ) : null}
                      <button type="button" className="de-btn de-btn--outline de-btn--xs" onClick={() => setCancel({ line: l })}>
                        Cancel
                      </button>
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {adding ? (
        <AddServiceModal
          booking={booking}
          purpose={adding.purpose}
          appointmentId={adding.appointmentId}
          initialOptions={adding.options}
          onClose={() => setAdding(null)}
          onDone={() => {
            setAdding(null);
            changed();
          }}
        />
      ) : null}
      {moving ? (
        <MoveBookingModal
          booking={booking}
          onClose={() => setMoving(false)}
          onMoved={() => {
            setMoving(false);
            changed();
          }}
        />
      ) : null}
      {cancel ? (
        <Confirm
          title={cancel.all ? 'Cancel the whole booking?' : `Cancel ${cancel.line.service_name}?`}
          message={
            <div>
              <div style={{ marginBottom: 10 }}>
                {cancel.all
                  ? 'Every service still booked is cancelled and its slots are freed. Waiting reminders stop.'
                  : 'This service’s slot is freed. The booking’s reminders move to its next service still booked.'}
              </div>
              <input className="de-input de-input--bordered" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" />
            </div>
          }
          confirmLabel="Cancel it"
          busy={busy === 'cancel'}
          onCancel={() => setCancel(null)}
          onConfirm={doCancel}
        />
      ) : null}
    </div>
  );
}
